import { Config } from "../config";
import { TIER } from "../utils/constants";
import Ajv from "ajv";

export function getPlatform() {
  const userAgent = navigator.userAgent.toLowerCase();

  if (/android/.test(userAgent)) {
      return "Android";
  } else if (/iphone|ipad|ipod/.test(userAgent)) {
      return "iOS";
  } else {
      return "Web";
  }
}

export function sendEvent(eventName, payload) {
  if (window && window.snowplow) {
    window.snowplow(eventName, payload)
  }
}

function getOSVersion() {
  const userAgent = navigator.userAgent;

  // macOS
  const macMatch = userAgent.match(/Mac OS X (\d+[_\.]\d+[_\.]?\d*)/);
  if (macMatch) return `macOS ${macMatch[1].replace(/_/g, '.')}`;

  // Windows
  const winMatch = userAgent.match(/Windows NT (\d+\.\d+)/);
  if (winMatch) return `Windows ${winMatch[1]}`;

  // Android
  const androidMatch = userAgent.match(/Android (\d+[\.\d]*)/);
  if (androidMatch) return `Android ${androidMatch[1]}`;

  // iOS
  const iosMatch = userAgent.match(/OS (\d+[_\d]*) like Mac OS X/);
  if (iosMatch) return `iOS ${iosMatch[1].replace(/_/g, '.')}`;

  return "Unknown OS";
}


export class Analytics {
  constructor(appversion) {
    this.isSnowplowEnabled = true;
    this.eventPayload = {
      appversion,
      platform: getPlatform(),
      osversion: getOSVersion(),
      "mb-s-sessionId": this.generateSessionId(),
      patientid: "", // setting it as empty string, as we don't call the userprofile API on reimbursement currently
      
    };
    // Initialize Ajv directly. No complex setup needed.
    this.validator = new Ajv();
  }

  async validatePayload(eventKey, fullPayload) {
    if (Config.TIER !== TIER.STAGING) {
      return true; // Only validate in staging
    }

    if (!this.validator) {
      console.error("AJV validator not available. Skipping payload validation.");
      return true; // Fail open if validator isn't ready
    }

    try {
      const schemaResponse = await fetch(`https://mb-prod-events-platform-iglu-server.medibuddy.in/api/schemas/com.medibuddy/${eventKey}`, {
        headers: { apikey: Config.SNOWPLOW_SCHEMA_API_KEY }
      });
      const schema = await schemaResponse.json();

      // Transform the fetched schema to include only the necessary properties for validation.
      const simplifiedSchema = {};
      const allowedKeys = ['properties', 'additionalProperties', 'type', 'required'];
      for (const key of allowedKeys) {
        if (Object.prototype.hasOwnProperty.call(schema, key)) {
          simplifiedSchema[key] = schema[key];
        }
      }

      // Compile the simplified schema and validate the payload directly.
      const validate = this.validator.compile(simplifiedSchema);
      const isValid = validate(fullPayload);

      // If the payload is valid, check for missing optional fields and log a warning.
      if (isValid) {
        const schemaProperties = Object.keys(simplifiedSchema.properties || {});
        const payloadProperties = Object.keys(fullPayload);
        const requiredProperties = simplifiedSchema.required || [];

        for (const prop of schemaProperties) {
          if (!payloadProperties.includes(prop) && !requiredProperties.includes(prop)) {
            console.warn(`Optional field '${prop}' is missing from the payload for event: ${eventKey}`);
          }
        }
      }

      if (!isValid) {
        console.error(`Payload validation failed for event: ${eventKey}`, validate.errors);
      }

      console.log(`Validation for ${eventKey} was successful:`, isValid);
      return isValid;

    } catch (error) {
      console.error(`An error occurred during payload validation for event: ${eventKey}`, error);
      return true; // Fail open on error
    }
  }

  sendToSnowplow(schema, payload) {
    if (!this.isSnowplowEnabled) return;

    setTimeout(async () => {
      const fullPayload = { ...this.eventPayload, ...payload };
      const eventKey = schema.replace("iglu:com.medibuddy/", "")
      if (await this.validatePayload(eventKey, fullPayload)) {
        sendEvent('trackSelfDescribingEvent', {
          event: {
            schema: schema,
            data: fullPayload,
          },
        });
      }
    }, 0);
  }

  generateSessionId() {
    const existingSessionId = sessionStorage.getItem("mbsessionid");
    if (existingSessionId) {
      return existingSessionId;
    }
    return Math.random().toString(36).substring(2, 15);
  }
}

/**
 * @param {Object} employeeDetailsFromAPI - Details of the current user from the API 
 */
export function prepareUserDetails(employeeDetailsFromAPI) {
  const { emailId, mbUserId, pmEntityId, uuid } = employeeDetailsFromAPI;

  return {
    email: emailId.toString(),
    mbuserid: mbUserId.toString(),
    pmentityid: pmEntityId.toString(),
    mbuuid: uuid.toString()
  }
}